import React from 'react';
import { Typography } from 'antd';
import { VisualSelectOption } from '../../../../../types/templates';
import { SLOT_STYLES } from '../../shared/slot-styles';
import { MediaImage } from '../../shared/media-image';

const { Text } = Typography;

interface ItemCardProps {
  option: VisualSelectOption;
  /** Which slot this card is currently filling (for color accent + pip). Null if unselected. */
  slotNum: 1 | 2 | null;
  /** Whether to render the numeric pip badge (dual-slot only). */
  showPip?: boolean;
  onClick: () => void;
}

/**
 * A clickable card in the search grid. Shows image (with optional backgroundColor),
 * label, and — when selected — applies the slot's color accent.
 *
 * Disabled options: grayed out, non-interactive, no hover effects.
 */
export const ItemCard: React.FC<ItemCardProps> = ({ option, slotNum, showPip = true, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  const styles = slotNum ? SLOT_STYLES[slotNum] : null;
  const disabled = !!option.disabled;

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    border: styles
      ? styles.border
      : !disabled && hovered
        ? '0.5px solid rgba(0,0,0,0.3)'
        : '0.5px solid rgba(0,0,0,0.12)',
    borderRadius: 8,
    padding: '8px 4px 6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    background: styles
      ? styles.background
      : !disabled && hovered
        ? 'rgba(0,0,0,0.03)'
        : 'var(--color-background-primary, #fff)',
    opacity: disabled ? 0.45 : 1,
    filter: disabled ? 'grayscale(0.6)' : undefined,
    transition: 'border-color 0.12s, background 0.12s'
  };

  const handleClick = () => {
    if (disabled) return;
    onClick();
  };

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {slotNum && showPip && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: styles!.pip.background,
            color: styles!.pip.color,
            fontSize: 9,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {slotNum}
        </div>
      )}

      {option.imageUrl ? (
        <div
          style={{
            width: 32,
            height: 32,
            background: option.backgroundColor ?? 'transparent',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <MediaImage
            src={option.imageUrl}
            alt={String(option.label ?? option.value)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 4,
            background: option.backgroundColor ?? 'rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.4)'
          }}
        >
          {String(option.label ?? option.value)
            .substring(0, 3)
            .toUpperCase()}
        </div>
      )}

      <Text
        style={{
          fontSize: 10,
          fontWeight: slotNum ? 500 : 400,
          color: styles ? styles.nameColor : undefined,
          textAlign: 'center',
          lineHeight: 1.2,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 2px',
          display: 'block'
        }}
      >
        {String(option.label ?? option.value)}
      </Text>
    </div>
  );
};
